/**
 * @fileoverview Optimized Loading Overlay for Authentication
 *
 * Performance-optimized loading overlay with proper memoization and
 * smooth animations for authentication flows. Reduces unnecessary
 * re-renders and provides better user experience.
 *
 * @author Claude Code
 * @since Phase 4 - Code Quality Improvements
 */

import React, { memo, useMemo } from 'react';
import { Spinner } from '@/components/ui/spinner';

/**
 * @typedef {Object} LoadingOverlayProps
 * @property {boolean} open - Whether the overlay is visible
 * @property {string} [message] - Loading message to display
 * @property {Function} [onClose] - Callback when overlay should close
 * @property {boolean} [persistent] - Whether overlay is persistent (no close on click)
 * @property {number} [size] - Size of the loading spinner
 * @property {string} [variant] - Visual variant ('default' | 'minimal' | 'detailed')
 */

/**
 * Optimized loading overlay component with memoization
 *
 * @param {LoadingOverlayProps} props - Component props
 * @returns {React.ReactElement|null} The loading overlay or null
 */
const OptimizedLoadingOverlay = memo(
  ({
    open = false,
    message = 'Loading...',
    onClose,
    persistent = false,
    size = 60,
    variant = 'default',
  }) => {
    // Memoize progress component to prevent unnecessary re-renders
    const progressComponent = useMemo(
      () => <Spinner size={size} className="text-primary" />,
      [size],
    );

    // Memoize message component
    const messageComponent = useMemo(() => {
      if (!message) return null;

      return <p>{message}</p>;
    }, [message, variant]);

    // Don't render anything if not open
    if (!open) return null;

    return (
      <div
        className={`fixed inset-0 flex items-center justify-center z-[1300] bg-black/70 backdrop-blur-[4px] text-white transition-opacity duration-300 ${
          open ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={persistent ? undefined : onClose}
      >
        <div
          className="flex flex-col items-center gap-4"
          style={
            variant === 'detailed'
              ? {
                  padding: 24,
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  borderRadius: 8,
                  backdropFilter: 'blur(8px)',
                }
              : undefined
          }
        >
          {progressComponent}
          {messageComponent}
        </div>
      </div>
    );
  },
);

// Display name for debugging
OptimizedLoadingOverlay.displayName = 'OptimizedLoadingOverlay';

/**
 * Hook for managing loading states with automatic cleanup
 *
 * @param {boolean} [initialLoading=false] - Initial loading state
 * @returns {Object} Loading state and controls
 * @returns {boolean} returns.isLoading - Current loading state
 * @returns {Function} returns.startLoading - Function to start loading
 * @returns {Function} returns.stopLoading - Function to stop loading
 * @returns {Function} returns.setLoading - Function to set loading state directly
 *
 * @example
 * const { isLoading, startLoading, stopLoading } = useLoadingState();
 *
 * const handleSubmit = async () => {
 *   startLoading();
 *   try {
 *     await submitData();
 *   } finally {
 *     stopLoading();
 *   }
 * };
 */
export const useLoadingState = (initialLoading = false) => {
  const [isLoading, setIsLoading] = React.useState(initialLoading);

  const startLoading = React.useCallback(() => {
    setIsLoading(true);
  }, []);

  const stopLoading = React.useCallback(() => {
    setIsLoading(false);
  }, []);

  const setLoading = React.useCallback(loading => {
    setIsLoading(Boolean(loading));
  }, []);

  return {
    isLoading,
    startLoading,
    stopLoading,
    setLoading,
  };
};

/**
 * Higher-order component that adds loading overlay to any component
 *
 * @param {React.ComponentType} WrappedComponent - Component to wrap
 * @param {Object} [defaultProps={}] - Default props for the loading overlay
 * @returns {React.ComponentType} Enhanced component with loading overlay
 *
 * @example
 * const MyComponentWithLoading = withLoadingOverlay(MyComponent, {
 *   variant: 'detailed',
 *   persistent: true
 * });
 */
export const withLoadingOverlay = (WrappedComponent, defaultProps = {}) => {
  const WithLoadingOverlay = memo(props => {
    const { loading, loadingMessage, ...restProps } = props;

    return (
      <>
        <WrappedComponent {...restProps} />
        <OptimizedLoadingOverlay
          open={loading}
          message={loadingMessage}
          {...defaultProps}
        />
      </>
    );
  });

  WithLoadingOverlay.displayName = `withLoadingOverlay(${WrappedComponent.displayName || WrappedComponent.name})`;

  return WithLoadingOverlay;
};

export default OptimizedLoadingOverlay;
